import { IChapter, ICourse, IDeliverableAnswer, IMCQAnswer, IModule } from '@shared';
import { CPF_TRAINING_TYPE_MARKER } from './e-learning-conf';

export const requiresDeliverables = (trainingType?: string): boolean => {
  return !trainingType || trainingType.toUpperCase().includes(CPF_TRAINING_TYPE_MARKER);
};

export const isDeliverableCourse = (course: ICourse): boolean => {
  return !!course.deliverableQuestions?.length;
};

export const filterVisibleCourses = (courses: ICourse[], trainingType?: string): ICourse[] => {
  if (requiresDeliverables(trainingType)) {
    return courses;
  }
  return courses.filter((course) => !isDeliverableCourse(course));
};

export const filterVisibleChapters = (chapters: IChapter[], trainingType?: string): IChapter[] => {
  if (requiresDeliverables(trainingType)) {
    return chapters;
  }
  return chapters
    .map((chapter) => ({ ...chapter, courses: filterVisibleCourses(chapter.courses, trainingType) }))
    .filter((chapter) => chapter.courses.length > 0);
};

export const getChaptersCourses = (chapters: IChapter[]): ICourse[] => {
  return chapters.reduce<ICourse[]>((courses, chapter) => [...courses, ...chapter.courses], []);
};

export const getFinalQuizCourse = (module: IModule): ICourse | undefined => {
  const lastCourse = getChaptersCourses(module.chapters)
    .filter((course) => !isDeliverableCourse(course))
    .pop();
  return lastCourse?.mcq?.length ? lastCourse : undefined;
};

export const isModuleCompleted = (
  module: IModule,
  progress: { completedCourses: string[]; deliverableAnswers: IDeliverableAnswer[]; MCQAnswers: IMCQAnswer[] },
  trainingType?: string
): boolean => {
  const finalQuiz = getFinalQuizCourse(module);
  if (finalQuiz && progress.MCQAnswers.some((answer) => answer.moduleId === finalQuiz.id)) {
    return true;
  }

  const courses = getChaptersCourses(module.chapters);
  const deliverableCourses = requiresDeliverables(trainingType) ? courses.filter(isDeliverableCourse) : [];
  const requiredCourses = courses.filter((course) => !isDeliverableCourse(course) && course.id !== finalQuiz?.id);

  if (finalQuiz && !requiredCourses.length && !deliverableCourses.length) {
    return false;
  }

  return (
    requiredCourses.every((course) => progress.completedCourses.includes(course.id)) &&
    deliverableCourses.every((course) => progress.deliverableAnswers.some((answer) => answer.moduleId === module.id && answer.courseId === course.id))
  );
};
